import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Feedback } from '@bonusly/common-feedback';
import { UserAvatar, UserPopover } from '@bonusly/common-user-popover';
import { Box, chakra, EyeSolid, Flex, Text, Tooltip, useBreakpointValue } from '@bonusly/design-system';

import RelativeTime from '../common/relative-time';
import { SpeechBubble } from './speech-bubble';

interface LinkedFeedbackProps {
	evaluation: Feedback;
}

const StyledLink = chakra(Link);

export const LinkedFeedback = ({ evaluation }: LinkedFeedbackProps) => {
	const { t } = useTranslation('recognition');
	const names = [evaluation.giver.name];
	const sizes = useBreakpointValue({ base: 'sm' as any, xs: 'lg' as any });

	return (
		<Box display="flex">
			{/* Passing in these properties directly is fine for now because the avatar displayed is always going to be the
      current user, where we don't need given/received information. However, we need to come up with a more robust
      solution for the user popover because having to provide this information all the time is tedious. I added a
      ticket for this to my personal epic. -Matt */}
			<UserPopover userId={evaluation.receiver.id}>
				<UserAvatar userId={evaluation.receiver.id} size={sizes} />
			</UserPopover>

			<SpeechBubble ml={2} pt={2} pb={3} px={3} bg="grayAlpha.150" boxShadow="none">
				<Flex alignItems="center" gap={2} mb={2}>
					<Text color="gray.700" size="sm" fontWeight="semibold">
						{evaluation.receiver.name}
					</Text>

					<RelativeTime color="gray.700" cursor="pointer" date={evaluation.createdAt} fontSize="sm" fontWeight="normal" />

					<Tooltip label={t('bonus_card.linked_feedback.privacy_tooltip')}>
						<Box as="button" display="flex" alignItems="center">
							<EyeSolid color="text.softer" size="sm" />
						</Box>
					</Tooltip>
				</Flex>

				<Text>
					{t('bonus_card.linked_feedback.title', { names })}{' '}
					<StyledLink to={`/feedback?evaluation_id=${evaluation.id}`} color="blue.500" fontWeight="medium">
						{t('bonus_card.linked_feedback.link_text')}
					</StyledLink>
				</Text>
			</SpeechBubble>
		</Box>
	);
};
